import React, { useState, useCallback, useMemo } from 'react';
import { useParams, useHistory, withRouter } from 'react-router-dom';
import { CatalogForm } from '../../../../components/CatalogForm/CatalogForm';
import { Validators } from '../../../../components/CatalogForm/validators';
import Http from '../../../../Http';
import Toast from 'react-hot-toast';

const PERSONA_TIPOS = [
  { value: '1', label: 'Persona Física' },
  { value: '2', label: 'Persona Moral' },
];

export const RemitenteNuevo = ({ match, cliente: propCliente, id: propId, onSave, onCancel }) => {
  const routeParams = useParams() || {};
  const history = useHistory();
  const clientId = propCliente || match?.params?.id || routeParams?.id;
  const remitenteId = propId || match?.params?.tabId || routeParams?.tabId;

  const [colonias, setColonias] = useState([]);
  const [localidades, setLocalidades] = useState([]);

  // Validación de RFC según tipo de persona
  const validateRfc = useCallback((val, allValues) => {
    if (!val) return null;
    const clean = String(val).trim().toUpperCase();
    const isFisica = String(allValues.cliente_rems_fm) === '1';

    if (isFisica) {
      const rfcFisicaRegex = /^[A-ZÑ&]{4}\d{6}[A-Z0-9]{3}$/;
      if (!rfcFisicaRegex.test(clean)) {
        return 'RFC de persona física inválido (debe tener 13 caracteres)';
      }
    } else {
      const rfcMoralRegex = /^[A-ZÑ&]{3}\d{6}[A-Z0-9]{3}$/;
      if (!rfcMoralRegex.test(clean)) {
        return 'RFC de persona moral inválido (debe tener 12 caracteres)';
      }
    }
    return null;
  }, []);

  // Carga de datos del remitente si estamos en modo edición
  const loadRemitenteData = useCallback(async () => {
    if (!remitenteId) return null;
    try {
      const res = await Http.get(`cliente-remitente/one-new/${remitenteId}`);
      if (res && !res.error && res.data) {
        const item = res.data;
        const cp = item.cliente_rems_codigo_postal || item.cliente_rems_cp;

        if (cp && String(cp).length === 5) {
          try {
            const formRes = await Http.get(`cliente/form-generales?cp=${cp}`);
            if (formRes?.data?.direccion) {
              const { direccion } = formRes.data;
              if (direccion.colonias) {
                setColonias(direccion.colonias.map((c) => ({ value: String(c.id), label: c.colonia })));
              }
              if (direccion.localidades) {
                setLocalidades(direccion.localidades.map((l) => ({ value: String(l.id_localidad || l.id), label: l.localidad })));
              }
            }
          } catch (e) {
            console.error('Error al precargar colonias de remitente', e);
          }
        }

        return {
          cliente_rems: item.cliente_rems || '',
          cliente_rems_nombre: item.cliente_rems_nombre || '',
          cliente_rems_telefono: item.cliente_rems_telefono || '',
          cliente_rems_domicilio: item.cliente_rems_domicilio || '',
          cliente_rems_cp: cp ? String(cp) : '',
          cliente_rems_id_colonia: item.cliente_rems_id_colonia ? String(item.cliente_rems_id_colonia) : '',
          cliente_rems_id_localidad: item.cliente_rems_id_localidad ? String(item.cliente_rems_id_localidad) : '',
          cliente_rems_estado: item.cliente_rems_estado || '',
          cliente_rems_ciudad: item.cliente_rems_ciudad || '',
          cliente_rems_fm: item.cliente_rems_fm ? String(item.cliente_rems_fm) : '1',
          cliente_rems_rfc: item.cliente_rems_rfc || '',
        };
      }
      return null;
    } catch (e) {
      console.error('Error al cargar remitente', e);
      return null;
    }
  }, [remitenteId]);

  // Manejo de cambio de Código Postal
  const handleCpChange = useCallback(async (val, { setFieldValue }) => {
    const cpStr = String(val || '').trim();
    if (cpStr.length === 5) {
      try {
        const res = await Http.get(`cliente/form-generales?cp=${cpStr}`);
        if (res?.data?.direccion) {
          const { direccion } = res.data;
          if (direccion.colonias) {
            const cols = direccion.colonias.map((c) => ({ value: String(c.id), label: c.colonia }));
            setColonias(cols);
            if (cols.length === 1) {
              setFieldValue('cliente_rems_id_colonia', cols[0].value);
            }
          }
          if (direccion.localidades) {
            setLocalidades(
              direccion.localidades.map((l) => ({ value: String(l.id_localidad || l.id), label: l.localidad }))
            );
          }
          if (direccion.estado?.estado) {
            setFieldValue('cliente_rems_estado', direccion.estado.estado);
          }
          if (direccion.ciudad?.ciudad) {
            setFieldValue('cliente_rems_ciudad', direccion.ciudad.ciudad);
          }
        }
      } catch (err) {
        console.error('Error al consultar CP para remitente', err);
      }
    } else {
      setColonias([]);
      setLocalidades([]);
      setFieldValue('cliente_rems_id_colonia', '');
      setFieldValue('cliente_rems_id_localidad', '');
      setFieldValue('cliente_rems_estado', '');
      setFieldValue('cliente_rems_ciudad', '');
    }
  }, []);

  const fields = useMemo(
    () => [
      {
        name: 'section_remitente',
        label: 'Información del Remitente',
        type: 'section',
        col: 12,
      },
      ...(remitenteId
        ? [
            {
              name: 'cliente_rems',
              label: 'Clave',
              type: 'text',
              col: 4,
              disabled: true,
            },
          ]
        : []),
      {
        name: 'cliente_rems_nombre',
        label: 'Nombre o Razón Social',
        type: 'text',
        col: remitenteId ? 8 : 12,
        required: true,
        validators: [Validators.required('El nombre del remitente es requerido')],
        placeholder: 'Nombre completo o razón social',
      },
      {
        name: 'cliente_rems_telefono',
        label: 'Teléfono',
        type: 'text',
        col: 6,
        placeholder: '10 dígitos',
      },
      {
        name: 'cliente_rems_domicilio',
        label: 'Calle y Número / Domicilio',
        type: 'text',
        col: 6,
        required: true,
        validators: [Validators.required('El domicilio es requerido')],
        placeholder: 'Calle, número exterior e interior',
      },
      {
        name: 'cliente_rems_cp',
        label: 'Código Postal',
        type: 'number',
        col: 3,
        required: true,
        validators: [Validators.required('El código postal es requerido')],
        helperText: '5 dígitos para autocompletar',
        onChange: handleCpChange,
      },
      {
        name: 'cliente_rems_id_colonia',
        label: 'Colonia',
        type: 'select',
        col: 5,
        required: true,
        validators: [Validators.required('La colonia es requerida')],
        options: colonias,
        placeholder: colonias.length ? 'Seleccione colonia' : 'Ingrese un CP válido',
      },
      {
        name: 'cliente_rems_id_localidad',
        label: 'Localidad',
        type: 'select',
        col: 4,
        options: localidades,
        placeholder: localidades.length ? 'Seleccione localidad' : 'Opcional',
      },
      {
        name: 'cliente_rems_estado',
        label: 'Estado',
        type: 'text',
        col: 6,
        disabled: true,
        placeholder: 'Asignado automáticamente por CP',
      },
      {
        name: 'cliente_rems_ciudad',
        label: 'Ciudad / Municipio',
        type: 'text',
        col: 6,
        disabled: true,
        placeholder: 'Asignado automáticamente por CP',
      },
      {
        name: 'section_rfc',
        label: 'Datos Fiscales SAT',
        type: 'section',
        col: 12,
      },
      {
        name: 'cliente_rems_fm',
        label: 'Tipo de Persona',
        type: 'select',
        col: 6,
        options: PERSONA_TIPOS,
      },
      {
        name: 'cliente_rems_rfc',
        label: 'R.F.C.',
        type: 'text',
        col: 6,
        placeholder: 'XAXX010101000',
        validators: [validateRfc],
      },
    ],
    [remitenteId, colonias, localidades, handleCpChange, validateRfc]
  );

  const handleSubmit = useCallback(
    async (values) => {
      try {
        const payload = {
          ...values,
          id_cliente: clientId,
          id_remitente: remitenteId || null,
        };
        const url = remitenteId ? 'cliente-remitente/edit' : 'cliente-remitente/save';
        const res = await Http.post(url, payload);
        if (!res.error) {
          Toast.success(remitenteId ? 'Remitente actualizado correctamente' : 'Remitente creado correctamente');
          return res;
        } else {
          Toast.error(res.errMsg || 'Error al guardar remitente');
          return { error: true, errMsg: res.errMsg };
        }
      } catch (err) {
        Toast.error('Error inesperado al guardar el remitente');
        return { error: true, errMsg: 'Error inesperado al guardar remitente' };
      }
    },
    [clientId, remitenteId]
  );

  const handleSuccess = useCallback(
    (response) => {
      if (onSave) {
        onSave(response?.data || response);
      }
      if (remitenteId && clientId) {
        history.push(`/Catalogo/Cliente/${clientId}/Remitentes`);
      }
    },
    [onSave, remitenteId, clientId, history]
  );

  const handleCancelClick = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else if (remitenteId && clientId) {
      history.push(`/Catalogo/Cliente/${clientId}/Remitentes`);
    }
  }, [onCancel, remitenteId, clientId, history]);

  return (
    <div className="mb-4">
      <CatalogForm
        title={remitenteId ? 'Editar Remitente' : 'Nuevo Remitente'}
        subtitle={
          remitenteId
            ? `Actualice los datos del remitente #${remitenteId}`
            : 'Capture los datos del remitente de origen para embarques del cliente'
        }
        fields={fields}
        id={remitenteId}
        loadData={loadRemitenteData}
        onSubmit={handleSubmit}
        onSuccess={handleSuccess}
        onCancel={handleCancelClick}
        submitButtonText={remitenteId ? 'Actualizar Remitente' : 'Guardar Remitente'}
      />
    </div>
  );
};

export default withRouter(RemitenteNuevo);
